import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  UserCircle,
  ChevronDown,
  Lock,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Home,
  Menu,
  Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn, getRoleDisplayName } from '@/lib/utils'
import { authService } from '@/lib/auth'
import { useTheme } from '@/contexts/ThemeContext'
import { ProfileDialog } from './ProfileDialog'
import { ChangePasswordDialog } from './ChangePasswordDialog'

interface HeaderProps {
  onMenuClick?: () => void
  showMenuButton?: boolean
  leftContent?: React.ReactNode
}

export function Header({ onMenuClick, showMenuButton = false, leftContent }: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const user = authService.getUser()

  const formatDateTime = useCallback(() => {
    const now = new Date()
    const weekday = now.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', '')
    const day = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    const date = `${day}. ${now.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
    const time = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })
    return { date, time }
  }, [])

  const [dateTime, setDateTime] = useState(formatDateTime)

  useEffect(() => {
    const timer = setInterval(() => setDateTime(formatDateTime()), 30000)
    return () => clearInterval(timer)
  }, [formatDateTime])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogout = () => {
    authService.clearAuth()
    navigate('/login')
  }

  return (
    <header className="bg-background dark:bg-background shadow-sm border-b border-border dark:border-border z-10">
      <div className="flex items-center h-16 px-4">
        {/* Mobile menu button */}
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden text-foreground dark:text-foreground mr-2"
          >
            <Menu className="h-6 w-6" />
          </Button>
        )}

        {/* Left content (logo, title, etc) */}
        {leftContent && <div className="flex items-center">{leftContent}</div>}

        {/* Clock and User menu - pushed to the right */}
        <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground dark:text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span className="font-mono whitespace-nowrap">
            <span className="hidden sm:inline">{dateTime.date} </span>
            {dateTime.time}
          </span>
        </div>

        <div className="relative" ref={userMenuRef}>
          <Button
            variant="ghost"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex flex-col items-start gap-0 px-3 py-2 h-auto text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-muted"
          >
            <div className="flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              <span className="font-semibold">{user?.username}</span>
              <ChevronDown className="h-4 w-4" />
            </div>
            <span className="hidden sm:block text-xs text-muted-foreground dark:text-muted-foreground ml-7">
              {user?.role ? getRoleDisplayName(user.role, user.role_display_name) : ''}
            </span>
          </Button>

          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-background dark:bg-card rounded-lg shadow-lg border border-border dark:border-border py-2 z-50">
              <button
                onClick={() => {
                  navigate('/')
                  setUserMenuOpen(false)
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-accent"
              >
                <Home className="h-4 w-4" />
                Ir al Inicio
              </button>
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  setProfileDialogOpen(true)
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-accent"
              >
                <UserCircle className="h-4 w-4" />
                Mi Perfil
              </button>

              <div className="border-t border-border dark:border-border my-2" />

              {/* Theme selector */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground mb-2">Tema</p>
                <div className="grid grid-cols-3 gap-1">
                  <button
                    onClick={() => setTheme('light')}
                    className={cn(
                      "flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-colors",
                      theme === 'light'
                        ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        : "text-muted-foreground dark:text-muted-foreground hover:bg-muted dark:hover:bg-accent"
                    )}
                  >
                    <Sun className="h-4 w-4" />
                    <span>Claro</span>
                  </button>
                  <button
                    onClick={() => setTheme('dark')}
                    className={cn(
                      "flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-colors",
                      theme === 'dark'
                        ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        : "text-muted-foreground dark:text-muted-foreground hover:bg-muted dark:hover:bg-accent"
                    )}
                  >
                    <Moon className="h-4 w-4" />
                    <span>Oscuro</span>
                  </button>
                  <button
                    onClick={() => setTheme('system')}
                    className={cn(
                      "flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-colors",
                      theme === 'system'
                        ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        : "text-muted-foreground dark:text-muted-foreground hover:bg-muted dark:hover:bg-accent"
                    )}
                  >
                    <Monitor className="h-4 w-4" />
                    <span>Auto</span>
                  </button>
                </div>
              </div>

              <div className="border-t border-border dark:border-border my-2" />

              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  setPasswordDialogOpen(true)
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-accent"
              >
                <Lock className="h-4 w-4" />
                Cambiar Contraseña
              </button>

              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  handleLogout()
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4" />
                Salir
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Dialogs */}
      <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </header>
  )
}
